import type { UpdateWhitelabelConfigBody } from "@nlc/shared-types";

import { useNavigate, useParams } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/button";
import { ConfirmationModal } from "@/components/confirmation-modal";
import { FullPageLoader } from "@/components/fullpage-loader";
import { toast } from "@/components/toast/toast-queue";
import { FeaturePageLayout } from "@/features/shared/layout/FeaturePageLayout";

import { WhitelabelForm } from "./components/WhitelabelForm";
import { useDeleteWhitelabelConfig } from "./shared/hooks/useDeleteWhitelabelConfig";
import { useGetWhitelabelConfig } from "./shared/hooks/useGetWhitelabelConfig";
import { useUpdateWhitelabelConfig } from "./shared/hooks/useUpdateWhitelabelConfig";

export function EditWhitelabelConfigPage() {
  const navigate = useNavigate();
  const { mutate: updateConfig } = useUpdateWhitelabelConfig();
  const { isPending: isDeleting, mutate: deleteConfig } =
    useDeleteWhitelabelConfig();
  const { configId } = useParams({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    from: "/_auth/_app-shell/whitelabel/$configId/edit" as any,
  });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { data: config, isLoading } = useGetWhitelabelConfig(configId);

  const handleSubmit = (data: UpdateWhitelabelConfigBody) => {
    updateConfig(
      { data, id: configId },
      {
        onError: (error) => {
          console.error("Failed to update whitelabel config", error);
          toast.add(
            {
              description:
                error.message || "Failed to update whitelabel config.",
              title: "Error",
              type: "error",
            },
            { timeout: 5000 },
          );
        },
        onSuccess: () => {
          toast.add(
            {
              description:
                "The whitelabel config has been successfully updated.",
              title: "Config Updated",
              type: "success",
            },
            { timeout: 5000 },
          );
          navigate({ to: "/whitelabel" as string });
        },
      },
    );
  };

  const handleDelete = () => {
    deleteConfig(configId, {
      onError: (error) => {
        console.error("Failed to delete whitelabel config", error);
        toast.add(
          {
            description: error.message || "Failed to delete whitelabel config.",
            title: "Error",
            type: "error",
          },
          { timeout: 5000 },
        );
      },
      onSuccess: () => {
        toast.add(
          {
            description: "The whitelabel config has been successfully deleted.",
            title: "Config Deleted",
            type: "success",
          },
          { timeout: 5000 },
        );
        setIsDeleteModalOpen(false);
        navigate({ to: "/whitelabel" as string });
      },
    });
  };

  if (isLoading) {
    return <FullPageLoader />;
  }

  return (
    <div className="p-xl">
      {config ? (
        <FeaturePageLayout
          headerContent={
            <Button
              className="ml-auto"
              color="destructive"
              onPress={() => setIsDeleteModalOpen(true)}
              size="lg"
              variant="tertiary"
            >
              Delete Config
            </Button>
          }
          title="Edit Whitelabel Config"
        >
          <WhitelabelForm
            initialData={config}
            isEditing={true}
            onCancel={() => navigate({ to: "/whitelabel" as string })}
            onSubmit={handleSubmit}
          />
        </FeaturePageLayout>
      ) : (
        <FeaturePageLayout title="Edit Whitelabel Config">
          <div className="p-6">Config not found</div>
        </FeaturePageLayout>
      )}
      <ConfirmationModal
        confirmButtonText={isDeleting ? "Deleting..." : "Delete"}
        icon={<Trash2 className="text-featured-icon-light-fg-error" />}
        isConfirmLoading={isDeleting}
        isOpen={isDeleteModalOpen}
        onConfirm={handleDelete}
        onOpenChange={setIsDeleteModalOpen}
        text="This whitelabel config will be deleted permanently and cannot be retrieved. All associated projects will be disassociated. Are you sure you want to delete?"
        title="Delete whitelabel config"
      />
    </div>
  );
}
